/**
 * Short scan of what the convoy can hear, then the operator picks own kit.
 *
 * Capture used to write every audible radio into the baseline. A car-park
 * AP is not ours just because it was next to us when we pressed the button.
 * Start a 60s listen so vehicle hotspots can come up, then pick from that
 * minute plus the minute before it.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Checkbox, CircularProgress, Typography } from '@mui/material';
import {
  BaselineCandidate,
  getBaselineCandidates,
  getMissionBaseline,
  trustIdentifiers,
} from '../../services/missionApi';
import { metalSx } from '../../theme/metalButtons';

const GOLD = '#f5b942';

/** Live listen so every assigned radio can check in. */
export const OWN_KIT_LISTEN_S = 60;
/** Also include what was heard this long before the listen started. */
export const OWN_KIT_PRIOR_S = 60;
export const OWN_KIT_WINDOW_S = OWN_KIT_LISTEN_S + OWN_KIT_PRIOR_S;

export interface OwnKitReviewProps {
  missionId: number;
  onError: (message: string) => void;
  onChanged?: () => void;
  /** Seconds of recent RF to list after the listen window. */
  seconds?: number;
  /** Live listen before reading the list. Default 60s. */
  listenSeconds?: number;
}

const OwnKitReview: React.FC<OwnKitReviewProps> = ({
  missionId, onError, onChanged, seconds = OWN_KIT_WINDOW_S, listenSeconds = OWN_KIT_LISTEN_S,
}) => {
  const [candidates, setCandidates] = useState<BaselineCandidate[] | null>(null);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(null);
  const cancelRef = useRef(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const listening = remaining != null;

  const loadHeard = useCallback(async () => {
    setCandidates(await getBaselineCandidates(missionId, seconds));
  }, [missionId, seconds]);

  const listenThenLoad = useCallback(async () => {
    cancelRef.current = false;
    setBusy(true);
    setRemaining(listenSeconds);
    const started = Date.now();
    await new Promise<void>((resolve) => {
      const tick = window.setInterval(() => {
        if (cancelRef.current) {
          window.clearInterval(tick);
          resolve();
          return;
        }
        const left = Math.max(0, listenSeconds - Math.floor((Date.now() - started) / 1000));
        setRemaining(left);
        if (left <= 0) {
          window.clearInterval(tick);
          resolve();
        }
      }, 250);
    });
    if (cancelRef.current) {
      setBusy(false);
      setRemaining(null);
      return;
    }
    try {
      await loadHeard();
    } catch (e: any) {
      onErrorRef.current(e?.response?.data?.detail || e?.message || 'Could not scan nearby radios');
      setCandidates([]);
    } finally {
      setRemaining(null);
      setBusy(false);
    }
  }, [listenSeconds, loadHeard]);

  useEffect(() => {
    cancelRef.current = false;
    setCandidates(null);
    setRemaining(null);
    setPicked({});
    return () => { cancelRef.current = true; };
  }, [missionId]);

  const open = useMemo(
    () => (candidates || []).filter((row) => !row.trusted),
    [candidates],
  );
  const selected = open.filter((row) => picked[row.identifier]);

  const toggle = (id: string) => {
    setPicked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const allOn = open.length > 0 && selected.length === open.length;

  const addSelected = async () => {
    if (!selected.length) return;
    setBusy(true);
    try {
      await trustIdentifiers(
        missionId,
        selected.map((row) => ({
          identifier: row.identifier,
          signal_type: row.signal_type,
          name: row.name,
          manufacturer_id: row.manufacturer_id ?? null,
        })),
      );
      setPicked({});
      await loadHeard();
      await getMissionBaseline(missionId).catch(() => null);
      onChanged?.();
    } catch (e: any) {
      onError(e?.response?.data?.detail || e?.message || 'Could not trust the selected radios');
    } finally {
      setBusy(false);
    }
  };

  if (candidates === null) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {listening
            ? `Listening ${remaining}s so every assigned device can check in…`
            : `Start a ${listenSeconds}s scan on the devices assigned to this mission. Anything they hear in that minute or the minute before can be marked ours.`}
        </Typography>
        <Button
          size="small"
          disabled={busy}
          onClick={listenThenLoad}
          sx={{ fontSize: '0.7rem', fontWeight: 800, ...metalSx('steel') }}
        >
          {listening
            ? <><CircularProgress size={12} sx={{ mr: 0.5 }} />{remaining}s</>
            : `Start ${listenSeconds}s scan`}
        </Button>
      </Box>
    );
  }

  if (candidates.length === 0) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {remaining != null
            ? `Listening ${remaining}s…`
            : 'Nothing audible. This needs an assigned phone or wardriver — a convoy of vehicle beacons collects no RF at all.'}
        </Typography>
        <Button size="small" disabled={busy} onClick={listenThenLoad} sx={{ mt: 0.5, fontSize: '0.68rem' }}>
          {busy ? `Listening ${remaining ?? listenSeconds}s` : `Listen ${listenSeconds}s`}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        Heard by assigned devices in the {listenSeconds}s listen or the {listenSeconds}s before it.
        Pick convoy radios to exclude. Nothing is trusted until you add it.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography
          variant="caption"
          role="button"
          tabIndex={0}
          onClick={() => {
            if (!open.length) return;
            const next = !allOn;
            setPicked(Object.fromEntries(open.map((row) => [row.identifier, next])));
          }}
          sx={{ fontWeight: 700, cursor: open.length ? 'pointer' : 'default', color: GOLD }}
        >
          {allOn ? 'Clear all' : 'Select all heard'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {open.length} new · {candidates.length - open.length} already ours
        </Typography>
        <Button size="small" disabled={busy} onClick={listenThenLoad} sx={{ fontSize: '0.65rem', py: 0 }}>
          {busy
            ? <><CircularProgress size={12} sx={{ mr: 0.5 }} />{remaining != null ? `${remaining}s` : ''}</>
            : `Listen ${listenSeconds}s`}
        </Button>
      </Box>
      <Box sx={{ display: 'grid', gap: 0.15, maxHeight: 240, overflowY: 'auto' }}>
        {candidates.map((row) => (
          <Box
            key={row.identifier}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.25, py: 0.1 }}
          >
            <Checkbox
              size="small"
              disabled={row.trusted || busy}
              checked={row.trusted || !!picked[row.identifier]}
              onChange={() => toggle(row.identifier)}
              sx={{ p: 0.25, color: GOLD, '&.Mui-checked': { color: GOLD } }}
            />
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {row.identifier}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
              {row.name || row.signal_type} · {row.sightings}
            </Typography>
            {row.trusted && (
              <Typography variant="caption" sx={{ color: GOLD }}>ours</Typography>
            )}
          </Box>
        ))}
      </Box>
      <Button
        size="small"
        disabled={busy || selected.length === 0}
        onClick={addSelected}
        sx={{ mt: 1, fontSize: '0.7rem', fontWeight: 800, ...metalSx('steel') }}
      >
        Add {selected.length || ''} selected
      </Button>
    </Box>
  );
};

export default OwnKitReview;
